import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PremiumHeader } from '@/components/premium/PremiumHeader';
import { SEO, OrganizationStructuredData, WebSiteStructuredData, FAQStructuredData, HowToStructuredData, LocalBusinessStructuredData, SpeakableStructuredData } from '@/components/SEO';
import { EnhancedKnowledgeGraph } from '@/components/KnowledgeGraph';
import { useTheme } from '@/context/ThemeContext';

// Import refactored components
import {
  ClassicHero,
  BridgeSection,
  ProformaInvoiceBannerClassic,
  ExperienceCentreClassic,
  WhyChooseMFS,
  MadeInIndiaSection,
  EzeefitPromiseSection,
  CategoriesSection,
  VideoShowcaseClassic,
  ClientFootprintSection,
  CustomerReviewsSection,
  CTASection,
  SEOContentSection,
  InstagramSectionClassic,
  ClassicFooter
} from '@/components/classic';

// Import ProductionUnitSection directly
import { ProductionUnitSection } from '@/components/classic/ProductionUnit';

// FAQ data for SEO
const HOME_FAQS = [
  {
    question: "Do you deliver office furniture outside Mumbai?",
    answer: "Yes, Modular Furniture Store delivers Pan India. Mumbai and Navi Mumbai get free delivery. Other locations have shipping charges calculated at checkout based on distance and order size."
  },
  {
    question: "How long does delivery take for modular office furniture?",
    answer: "Delivery times range from 7-25 business days depending on order value and stock availability. Orders under ₹50,000 are delivered in 7 days, while larger orders may take up to 25 days."
  },
  {
    question: "Do you offer installation services for workstations and privacy booths?",
    answer: "Yes, we provide free professional installation in Mumbai and Navi Mumbai. Our modular furniture is designed with 'Kholo, Jodo, Aur Lagalo' (Open, Join, Install) method requiring no tools."
  },
  {
    question: "What is the warranty on modular office furniture?",
    answer: "All products from Modular Furniture Store come with manufacturer warranty. Warranty periods vary by product category. Contact our customer service for specific warranty details."
  },
  {
    question: "Can I customize the color and size of office furniture?",
    answer: "Yes, most of our modular workstations, privacy booths, and storage solutions are available in multiple color options. Custom sizes can be discussed with our sales team."
  }
];

export default function Home() {
  const [policyPopup, setPolicyPopup] = useState(false);
  const navigate = useNavigate();
  const { setHomepageTheme, THEMES } = useTheme();

  const switchToPremium = () => {
    setHomepageTheme(THEMES.PREMIUM);
    window.scrollTo(0, 0);
    navigate('/home-premium');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-secondary to-background">
      {/* Animation Styles */}
      <style>{`
        @keyframes scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @keyframes scroll-reverse {
          0% { transform: translateX(-50%); }
          100% { transform: translateX(0); }
        }
        .animate-scroll {
          animation: scroll 30s linear infinite;
        }
        .animate-scroll-reverse {
          animation: scroll-reverse 30s linear infinite;
        }
      `}</style>

      {/* SEO Components */}
      <SEO 
        title="Buy Office Furniture Online | Modular Workstation | Office Privacy Pods Mumbai & Pan India"
        description="Buy modular workstations, office phone booths, focus pods & acoustic pods online. Shop soundproof privacy booths, meeting pods, workstation desks, modular office cubicles. Best workstation desk price. Free shipping in Mumbai & Navi Mumbai."
        keywords="modular workstations, office phone booths, workstation desks, modular office cubicles, phone pods, focus pods, buy privacy booth, shop modular workstations, order office pods, buy office storage, workstation desk price, shop meeting pods, buy soundproof booths, shop acoustic pods, privacy booth cost, buy office furniture online, soundproof office meeting pods, office furniture store mumbai"
        canonical="/"
      />
      <OrganizationStructuredData />
      <WebSiteStructuredData />
      <LocalBusinessStructuredData />
      <HowToStructuredData />
      <FAQStructuredData faqs={HOME_FAQS} />
      <SpeakableStructuredData 
        headline="Modular Furniture Store - Premium Office Furniture Made in India"
        summary="Buy modular workstations, privacy booths, and office furniture online. Free delivery in Mumbai. Kholo, Jodo, Aur Lagalo - tool-free assembly."
      />
      <EnhancedKnowledgeGraph />
      
      {/* Navigation */}
      <PremiumHeader />

      {/* Hero Section */}
      <ClassicHero />

      {/* Bridge Section - One Legacy, Two Ways */}
      <BridgeSection />

      {/* B2B Proforma Invoice Banner */}
      <ProformaInvoiceBannerClassic />

      {/* Experience Centre Section */}
      <ExperienceCentreClassic />

      {/* Why Choose MFS Section */}
      <WhyChooseMFS />

      {/* Made in India Trust Section */}
      <MadeInIndiaSection />

      {/* Categories Section */}
      <CategoriesSection />

      {/* Production Unit - Our Manufacturing Facility */}
      <ProductionUnitSection />

      {/* Client Footprint - Trusted by Industry Leaders */}
      <ClientFootprintSection />

      {/* The Ezeefit Promise Section */}
      <EzeefitPromiseSection />

      {/* Customer Reviews Section */}
      <CustomerReviewsSection />

      {/* CTA Section */}
      <CTASection />

      {/* SEO Content Section */}
      <SEOContentSection />

      {/* Video Showcase */}
      <VideoShowcaseClassic />

      {/* Instagram Section */}
      <InstagramSectionClassic />

      {/* Footer */}
      <ClassicFooter 
        switchToPremium={switchToPremium}
        policyPopup={policyPopup}
        setPolicyPopup={setPolicyPopup}
      />
    </div>
  );
}
